import React from 'react';
import { Pressable, View } from 'react-native';

import Text from '../../components/text/Text';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import MachineChip from './MachineChip';
import StatusBadge from './StatusBadge';
import StatusIcon, { STEP_STATUS } from './StatusIcon';
import Tag from './Tag';
import WarningBar from './WarningBar';

/**
 * StopRow: one stop (machine) on the run list.
 * Props:
 * - title: the position, e.g. "Level 2 · Lifts 9 & 10 (Left)" (site name sits in SiteGroupHeader)
 * - place: optional line above the title when the row is not under a site header
 * - machineNumber: shown as a readable MachineChip (SPEC decision 6)
 * - details: e.g. "Restock & repair · In progress"
 * - labels: classic run-list labels, strings or Tag props ({ label, tone }),
 *   e.g. ['1 ticket', 'Collect cash', { label: 'Coins required', tone: 'warning' }]
 * - status: 'done' (compact) | 'current' | 'todo'
 * - number: optional stop number shown in the left circle instead of the status
 *   circle (read-only lists where the run order matters, e.g. Run overview)
 * - isNow (default: status === 'current'): shows the "Now" badge
 * - warnings: site requirements for this stop (strings), shown as a yellow bar
 *   joined directly on top of the row so it clearly belongs to it
 * - showCompletedDetails (default false): keep the service details visible on completed history stops
 * - onPress, accessibilityHint, style
 */
const StopRow = ({
	title,
	place,
	machineNumber,
	details,
	labels,
	status = STEP_STATUS.TODO,
	number,
	isNow,
	warnings,
	onPress,
	accessibilityHint,
	showCompletedDetails = false,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const isDone = status === STEP_STATUS.DONE;
	const showDetails = !isDone || showCompletedDetails;
	const isCurrent = status === STEP_STATUS.CURRENT;
	const showNow = isNow ?? isCurrent;
	const hasWarnings = Boolean(warnings?.filter(Boolean).length);
	const labelList = (labels || []).filter(Boolean).map(label => (typeof label === 'string' ? { label } : label));

	const accessibilityLabel = [
		number && `Stop ${number}`,
		place,
		title,
		machineNumber && `machine ${machineNumber}`,
		showDetails && details,
		!isDone && labelList.map(label => label.label).join(', '),
		isDone && 'done',
		showNow && 'now',
	].filter(Boolean).join(', ');

	return (
		<View style={style}>
			{hasWarnings && <WarningBar items={warnings} size="compact" attached="bottom" />}
			<Pressable
				onPress={onPress}
				disabled={!onPress}
				accessibilityRole={onPress ? 'button' : undefined}
				accessibilityLabel={accessibilityLabel}
				accessibilityHint={accessibilityHint}
				style={({ pressed }) => [
					styles.row,
					styles[status],
					!isDone && styles.rowOpen,
					hasWarnings && styles.attachedTop,
					pressed && styles.pressed,
				]}
			>
				{number ? (
					<View style={[styles.number, isDone && styles.numberDone]}>
						<Text style={[styles.numberText, isDone && styles.numberTextDone]}>{number}</Text>
					</View>
				) : (
					<StatusIcon status={status} size={22} />
				)}
				<View style={styles.body}>
					{Boolean(place) && <Text style={styles.place}>{place}</Text>}
					<Text style={[styles.title, isCurrent && styles.titleCurrent, isDone && styles.titleDone]}>
						{title}
					</Text>
					{(Boolean(machineNumber) || (showDetails && Boolean(details))) && (
						<View style={styles.metaRow}>
							<MachineChip number={machineNumber} muted={isDone} />
							{showDetails && Boolean(details) && (
								<Text style={[styles.details, isCurrent && styles.detailsCurrent]}>{details}</Text>
							)}
						</View>
					)}
					{!isDone && labelList.length > 0 && (
						<View style={styles.labels}>
							{labelList.map(label => <Tag key={label.label} {...label} />)}
						</View>
					)}
				</View>
				{showNow && <StatusBadge label="Now" tone="primary" size="small" />}
			</Pressable>
		</View>
	);
};

const createStyles = ({ ui: { colors, radius, type, space } }) => ({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		minHeight: 48,
		paddingHorizontal: 14,
		paddingVertical: space.sm,
		borderRadius: radius.card,
		borderWidth: 1,
	},
	rowOpen: {
		minHeight: 68,
		paddingVertical: 10,
	},
	done: {
		backgroundColor: colors.card,
		borderColor: colors.card,
	},
	todo: {
		backgroundColor: colors.raised,
		borderColor: colors.raisedBorder,
	},
	current: {
		backgroundColor: colors.current,
		borderColor: colors.currentBorder,
		borderWidth: 2,
	},
	attachedTop: {
		borderTopLeftRadius: 0,
		borderTopRightRadius: 0,
	},
	pressed: {
		opacity: 0.85,
	},
	number: {
		minWidth: 28,
		height: 28,
		paddingHorizontal: space.xs,
		borderRadius: radius.pill,
		borderWidth: 2,
		borderColor: colors.iconMuted,
		alignItems: 'center',
		justifyContent: 'center',
	},
	numberDone: {
		borderColor: colors.success,
	},
	numberText: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	numberTextDone: {
		color: colors.success,
	},
	body: {
		flex: 1,
		gap: 5,
	},
	place: {
		...type.caption,
		color: colors.textSecondary,
	},
	title: {
		...type.rowTitle,
		color: colors.text,
	},
	titleCurrent: {
		...type.rowTitleSemibold,
	},
	titleDone: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
	metaRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: space.sm,
	},
	details: {
		...type.caption,
		color: colors.textSecondary,
		flexShrink: 1,
	},
	detailsCurrent: {
		color: colors.currentSubtle,
	},
	labels: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 6,
	},
});

export default StopRow;
